import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField, sectionChromeFields } from '../_shared/universal.js';
/** The universal custom-palette pair (`Background` / `Text`), kept beside the select that reveals them. */
const CUSTOM_COLOR_IDS = ['customBackground', 'customText'];
/**
 * collection-banner — the BANNER that sits at the very top of a collection page (Shrine's
 * `main-collection-banner.liquid`). It is the collection's masthead: the collection TITLE, optionally
 * its DESCRIPTION, and optionally its cover IMAGE behind/above the title. It is NOT a product grid — the
 * grid is a separate `main-collection` section. Shrine ships exactly 4 settings here (one of them a
 * no-op informational paragraph), no child blocks, and no presets (it is a required `main-*` template
 * section added by default to the collection template, not addable/orderable via the theme editor).
 *
 * CONFIG-BY-CONFIG with Shrine:
 *  - `paragraph` (informational only, no id) → modelled as a non-persisted `info` line on the
 *    `showCollectionImage` field (we have no standalone "paragraph" control; the help text carries the
 *    same merchandising note Shrine's paragraph carries — see field below).
 *  - `show_collection_description : checkbox : default true`  → `showCollectionDescription`.
 *  - `show_collection_image : checkbox : default false (+info)` → `showCollectionImage` (+ the info text).
 *  - `color_scheme : select : default background-1` (Shopify theme color-scheme system) → `colorScheme`,
 *    our 5-value Shrine-parity scheme select that maps onto the universal `data-scheme` palette.
 *
 * The universal "Section" padding groups are appended; the Shrine `color_scheme` above is the SAME
 * control as the universal one, so we DON'T also spread the universal colorScheme twice — `universalFields`
 * here is filtered to drop its `colorScheme` (the Shrine 5-value scheme is the single source of truth, no
 * duplicate "Color scheme" control) AND its `width` (this masthead's `.page-width` is hard-capped at the
 * theme container, so a Width select would render nothing — see Component.tsx `.page-width`).
 */
export const collectionBannerSchema = {
    type: 'collection-banner',
    name: 'Collection banner',
    version: 1,
    templates: ['collection'],
    fields: [
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'showCollectionDescription',
            type: 'checkbox',
            label: 'Show collection description',
            group: 'Content',
            default: true,
        },
        {
            id: 'showCollectionImage',
            type: 'checkbox',
            label: 'Show collection image',
            group: 'Content',
            // Shrine's informational `paragraph` lives next to this toggle — it tells the merchant where the
            // image comes from. We carry the same guidance as this field's `info` (we have no standalone
            // paragraph control; `info` renders the identical merchandising note in the inspector).
            info: 'Add a featured image to your collection in the admin to show it here. Images may not display in all themes.',
            default: false,
        },
        // ── Color scheme (Shrine `color_scheme`, 5 options + ours) ───────────────
        // Maps onto our universal `data-scheme` palette: background-1/background-2 → light (bg vs surface),
        // accent-1/accent-2 → accent, inverse → inverse. Default `background-1` matches Shrine.
        //
        // `custom` is OURS, not Shrine's (18/09/2026). Shrine's 5 palettes are the ones defined in the theme
        // editor, so a banner could never take a one-off brand colour — the seller's report was "je vois pas
        // l'option de mettre ma couleur de background custom, j'ai les choix limités". Every other section
        // spreads the universal chrome, whose scheme select ends in "Custom" + a Background/Text colour pair;
        // this section declares its own select for Shrine parity and so was the ONE band stuck on 5 fixed
        // palettes. The universal `customBackground`/`customText` fields are already in `fields` below (they
        // ride in on `universalFields`, hidden behind `visibleWhen colorScheme === 'custom'`), so adding the
        // option here is what turns them on; Component.tsx spreads `customSchemeVars` to paint them.
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Section',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // The universal custom-palette pair, lifted OUT of the spread below so it sits directly under the
        // select that reveals it. Left in place it landed under "Image corners" — the seller opens "Custom"
        // and the two colours appear one control further down, under an unrelated heading.
        ...sectionChromeFields.filter((f) => CUSTOM_COLOR_IDS.includes(f.id)),
        ...textColorFields(),
        // ── Section (universal) — padding; the colorScheme above replaces the universal SELECT, and this
        // fixed-width Shrine masthead has no width control (its `.page-width` is hard-capped at the theme
        // container by design — a `Width` select here would be a no-op), so we drop `width` too. ──
        mediaRadiusField,
        ...universalFields.filter((f) => f.id !== 'colorScheme' && f.id !== 'width' && !CUSTOM_COLOR_IDS.includes(f.id)),
    ],
    // Shrine has NO blocks for this section.
    blocks: [],
};
/**
 * Shrine ships NO `presets` for `main-collection-banner` (it is a required main-* template section, not
 * an addable/orderable preset). We still expose ONE preset so the banner is insertable in OUR builder and
 * the default collection template can seed it — faithful defaults: description on, image off,
 * background-1 scheme (exactly Shrine's schema defaults).
 */
export const collectionBannerPresets = [
    {
        name: 'Collection banner',
        settings: {
            showCollectionDescription: true,
            showCollectionImage: false,
            colorScheme: 'background-1',
        },
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const collectionBannerMeta = {
    label: 'Collection banner',
    group: 'Collection',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map